import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  RefreshCw,
  Activity,
  Mail,
  Shield,
  Sparkles,
  Unplug,
  LogOut,
  ArrowRight,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BetaBanner from '../components/BetaBanner';
import LiveTodayPanel from '../components/LiveTodayPanel';
import YouTubeCard from '../components/YouTubeCard';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

function fmtMoney(v) {
  if (v == null) return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (Number.isNaN(n)) return v;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
function fmtDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}
function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const EXEC_STATUS = {
  filled: { label: 'Filled', tone: 'ok' },
  partially_filled: { label: 'Partially filled', tone: 'ok' },
  submitted: { label: 'Submitted', tone: 'warn' },
  skipped: { label: 'Skipped', tone: 'mute' },
  failed: { label: 'Failed', tone: 'bad' },
  rejected: { label: 'Rejected', tone: 'bad' },
  expired: { label: 'Unfilled / expired', tone: 'warn' },
  canceled: { label: 'Canceled', tone: 'mute' },
};
const SKIP_LABEL = {
  paper_auto_trading_off: 'Auto-trading off',
  not_connected: 'Alpaca not connected',
  token_missing: 'Token missing — reconnect',
  stocks_disabled: 'Stocks disabled',
  account_blocked: 'Account blocked',
  trading_blocked: 'Trading blocked',
  insufficient_buying_power: 'Insufficient buying power',
  below_min_notional: 'Below minimum notional',
  signal_stale: 'Signal stale',
  duplicate_signal: 'Duplicate signal',
  live_mode_blocked: 'Live mode blocked (beta)',
  live_mode_blocked_during_beta: 'Live mode blocked (beta)',
  no_matching_position: 'No matching open position',
  invalid_close_pct: 'Invalid close percentage',
};

// ---------------- Tiny sparkline (same shape used on homepage) ----------------
function Sparkline({ values, width = 720, height = 120 }) {
  if (!values || values.length < 2) {
    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <line x1="0" y1={height - 16} x2={width} y2={height - 16} stroke="rgba(201,162,75,0.16)" strokeDasharray="3 4" />
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#A99563" fontFamily="JetBrains Mono" fontSize="11">No fills today yet</text>
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const dx = width / (values.length - 1);
  const pts = values.map((v, i) => [i * dx, height - 10 - ((v - min) / range) * (height - 20)]);
  const path = pts.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(' ');
  const last = pts[pts.length - 1];
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="dash-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C9A24B" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#C9A24B" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L ${width} ${height} L 0 ${height} Z`} fill="url(#dash-spark-fill)" />
      <path d={path} fill="none" stroke="#C9A24B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill="#E8C874">
        <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// ---------------- Dashboard ----------------
export default function Dashboard() {
  const { user, loading, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [positions, setPositions] = useState(null);
  const [latestSignal, setLatestSignal] = useState(null);
  const [signalHistory, setSignalHistory] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [todayLine, setTodayLine] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(null);

  useEffect(() => {
    if (!loading && !user) {
      sessionStorage.setItem('post_login_redirect', '/dashboard');
      navigate('/login?next=/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  const loadAccount = useCallback(async () => {
    setAccountLoading(true);
    try {
      const [accRes, livePosRes, cachedPosRes] = await Promise.all([
        api.get('/alpaca/account'),
        api.get('/alpaca/positions').catch(() => ({ data: null })),
        api.get('/positions/me').catch(() => ({ data: null })),
      ]);
      setAccount(accRes.data);
      const live = livePosRes.data;
      if (live && live.connected && Array.isArray(live.items)) {
        setPositions({ items: live.items, count: live.items.length, totals: live.totals });
      } else if (cachedPosRes.data && Array.isArray(cachedPosRes.data.items)) {
        setPositions({ items: cachedPosRes.data.items, count: cachedPosRes.data.count, totals: cachedPosRes.data.totals });
      } else {
        setPositions(null);
      }
    } catch (e) {
      setAccount({ connected: false, error: 'fetch_failed' });
    } finally { setAccountLoading(false); }
  }, []);

  const loadSignals = useCallback(async () => {
    try {
      const [latestRes, histRes, execRes, liveRes] = await Promise.all([
        api.get('/signals/latest'),
        api.get('/signals/history', { params: { limit: 10 } }),
        api.get('/signals/executions/me', { params: { limit: 10 } }),
        api.get('/public/live_today').catch(() => ({ data: { sparkline: [] } })),
      ]);
      setLatestSignal(latestRes.data?.signal || null);
      setSignalHistory(histRes.data?.items || []);
      setExecutions(execRes.data?.items || []);
      setTodayLine((liveRes.data?.sparkline || []).map((p) => p.v));
    } catch (e) { /* leave last */ }
  }, []);

  useEffect(() => { if (user) { loadAccount(); loadSignals(); } }, [user, loadAccount, loadSignals]);

  useEffect(() => {
    if (search.get('alpaca') === 'connected') {
      toast.success('Alpaca paper account connected.');
      loadAccount(); refresh();
    }
  }, [search, refresh, loadAccount]);

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Alpaca paper account from Talas? Your Alpaca account itself stays intact.')) return;
    setActionBusy(true);
    try {
      await api.post('/alpaca/disconnect');
      toast.success('Alpaca disconnected.');
      await refresh();
      setAccount({ connected: false });
    } catch (e) { toast.error('Could not disconnect. Try again.'); }
    finally { setActionBusy(false); }
  };

  const handleTestSignal = async () => {
    setActionBusy(true);
    try {
      await api.post('/signals/test');
      await loadSignals();
      toast.success('Test signal created.');
    } catch (e) { toast.error('Could not create test signal.'); }
    finally { setActionBusy(false); }
  };

  const handleSettingToggle = async (key, newValue) => {
    if (key === 'options_enabled') { toast.info('Options are disabled during beta.'); return; }
    if (key === 'paper_auto_trading' && newValue === true) {
      const ok = window.confirm(
        'Enable paper auto-trading?\n\nTalas will automatically place limit orders in your connected Alpaca paper account when bot signals arrive. No live funds are used. You can turn this off any time.'
      );
      if (!ok) return;
    }
    setSettingsBusy(key);
    try {
      await api.patch('/me/settings', { [key]: newValue });
      await refresh();
      toast.success('Settings updated.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update settings.'); }
    finally { setSettingsBusy(null); }
  };

  const execBySignal = useMemo(() => executions.reduce((acc, e) => {
    if (e.signal_id) acc[e.signal_id] = e;
    return acc;
  }, {}), [executions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <p className="text-[#A99563] text-sm font-mono">Loading…</p>
      </div>
    );
  }
  if (!user) return null;

  const connected = account?.connected;
  const settings = user.settings || {};

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex flex-col">
      <BetaBanner />
      <Navbar />

      <main className="flex-1">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-6 lg:py-8">

          {/* ----- Top row: greeting (left) + setup video (right) ----- */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start" data-testid="dashboard-header">
            <div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#F1ECDF] tracking-tight">
                    Welcome back<span className="gold-text">.</span>
                  </h1>
                  <div className="mt-2.5 inline-flex flex-wrap items-center gap-2 px-3 py-1.5 border border-[rgba(201,162,75,0.16)] rounded-sm bg-[#101012]">
                    <Mail size={12} className="gold-text" strokeWidth={1.8} />
                    <span className="font-mono text-xs text-[#F1ECDF]">{user.email}</span>
                    <span className="h-3 w-px bg-[rgba(201,162,75,0.16)]" />
                    <span className="text-[10px] font-mono text-[#A99563]">PAPER</span>
                    {user.isAdmin && (
                      <>
                        <span className="h-3 w-px bg-[rgba(201,162,75,0.16)]" />
                        <Link to="/admin/signals" data-testid="dashboard-admin-link" className="text-[10px] font-mono gold-text hover:text-[#F1ECDF] inline-flex items-center gap-1">
                          <Shield size={10} /> ADMIN
                        </Link>
                      </>
                    )}
                    <span className="h-3 w-px bg-[rgba(201,162,75,0.16)]" />
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono">
                      <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-[#7FC58C] dot-pulse' : 'bg-[#A99563]'}`} />
                      <span className={connected ? 'pnl-positive' : 'text-[#A99563]'}>
                        {connected ? 'Connected' : 'Not connected'}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { loadAccount(); loadSignals(); }}
                    data-testid="dashboard-refresh-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[rgba(201,162,75,0.16)] text-[#CDBE94] hover:text-[#F1ECDF] hover:border-[#C9A24B]/40 rounded-sm transition-colors"
                  >
                    <RefreshCw size={12} className={accountLoading ? 'animate-spin' : ''} />
                    Refresh
                  </button>
                  <button
                    onClick={logout}
                    data-testid="dashboard-logout-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[rgba(201,162,75,0.16)] text-[#A99563] hover:text-[#F1ECDF] rounded-sm transition-colors"
                  >
                    <LogOut size={12} />
                    Log Out
                  </button>
                </div>
              </div>
            </div>

            {/* Setup video — top-right on desktop; stacks below on mobile */}
            <div className="w-full">
              <YouTubeCard
                videoId="3Z48kwbaKmo"
                title="Set up your Alpaca account"
                header={{
                  eyebrow: 'SETUP GUIDE',
                  title: 'Set up your Alpaca account',
                }}
                testid="dashboard-setup-video"
              />
            </div>
          </div>

          {/* ----- Slim paper-trading notice (single line, not boxed) ----- */}
          <p className="mt-3 text-xs text-[#A99563] flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-[#C9A24B]" />
            <span><strong className="text-[#CDBE94] font-semibold">Paper trading only.</strong> No live funds are used during beta. All signals are simulated and orders go to your Alpaca paper account.</span>
          </p>

          {/* ----- 4-metric row ----- */}
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="metrics-row">
            <Metric label="Portfolio value"  value={fmtMoney(account?.portfolio_value)} testid="metric-portfolio-value" />
            <Metric
              label="Unrealized P&L"
              value={fmtMoney(positions?.totals?.unrealized_pnl)}
              colorize={positions?.totals?.unrealized_pnl}
              testid="metric-unrealized-pnl"
            />
            <Metric label="Open positions" value={positions?.count ?? 0} testid="metric-open-positions" />
            <Metric label="Buying power"   value={fmtMoney(account?.buying_power)} testid="metric-buying-power" />
          </div>

          {/* ----- Today (THIS USER's filled bot signals only) ----- */}
          <div className="mt-5" data-testid="today-chart">
            <LiveTodayPanel variant="dashboard" dataSource="personal" />
          </div>

          {/* ----- Open positions ----- */}
          <div className="mt-5 bg-[#101012] border border-[rgba(201,162,75,0.16)] rounded-md p-5" data-testid="positions-panel">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.3em] font-mono text-[#A99563] uppercase">Open positions</span>
              <span className="text-[10px] font-mono text-[#A99563]">{positions?.count ?? 0}</span>
            </div>
            {positions && positions.count > 0 ? (
              <>
                {/* Desktop table */}
                <div className="hidden md:block mt-4 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="positions-table">
                    <thead>
                      <tr className="text-left text-[10px] tracking-[0.25em] font-mono text-[#A99563] border-b border-[rgba(201,162,75,0.16)]">
                        <th className="py-2 pr-3">SYMBOL</th>
                        <th className="py-2 pr-3">QTY</th>
                        <th className="py-2 pr-3">MKT VALUE</th>
                        <th className="py-2">P&L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.items.map((p) => {
                        const pnl = p.unrealized_pnl;
                        const cls = pnl == null ? 'text-[#A99563]' : pnl >= 0 ? 'pnl-positive' : 'pnl-negative';
                        return (
                          <tr key={p.symbol} className="border-b border-[rgba(201,162,75,0.08)] last:border-0" data-testid={`position-${p.symbol}`}>
                            <td className="py-2.5 pr-3 font-mono text-[#F1ECDF]">{p.symbol}</td>
                            <td className="py-2.5 pr-3 font-mono tabular-nums text-[#CDBE94]">{p.qty}</td>
                            <td className="py-2.5 pr-3 font-mono tabular-nums text-[#CDBE94]">{fmtMoney(p.market_value)}</td>
                            <td className={`py-2.5 font-mono tabular-nums ${cls}`}>
                              {pnl == null ? '—' : `${pnl >= 0 ? '+' : '−'}${fmtMoney(Math.abs(pnl))}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden mt-4 space-y-2">
                  {positions.items.map((p) => {
                    const pnl = p.unrealized_pnl;
                    const cls = pnl == null ? 'text-[#A99563]' : pnl >= 0 ? 'pnl-positive' : 'pnl-negative';
                    return (
                      <div key={p.symbol} className="border border-[rgba(201,162,75,0.16)] rounded-sm p-3 flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="font-mono text-[#F1ECDF]">{p.symbol}</p>
                          <p className="text-[11px] font-mono text-[#A99563] mt-0.5">{p.qty} · {fmtMoney(p.market_value)}</p>
                        </div>
                        <span className={`font-mono tabular-nums ${cls}`}>
                          {pnl == null ? '—' : `${pnl >= 0 ? '+' : '−'}${fmtMoney(Math.abs(pnl))}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="mt-4 text-[12px] text-[#A99563]">No open positions yet.</p>
            )}
          </div>

          {/* ----- Auto-trade controls + Connection status side-by-side ----- */}
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
            <div className="bg-[#101012] border border-[rgba(201,162,75,0.16)] rounded-md p-5" data-testid="user-settings-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] tracking-[0.3em] font-mono text-[#A99563] uppercase">Auto-trade controls</span>
              </div>
              <div className="divide-y divide-[rgba(201,162,75,0.08)]" data-testid="settings-toggles">
                <ToggleRow testid="toggle-paper-auto-trading" label="Paper auto-trading"
                  description={settings.paper_auto_trading ? 'Talas will execute paper orders for you.' : 'Signals only — no orders placed.'}
                  enabled={!!settings.paper_auto_trading} busy={settingsBusy === 'paper_auto_trading'}
                  onToggle={(v) => handleSettingToggle('paper_auto_trading', v)} />
                <ToggleRow testid="toggle-signal-alerts" label="Signal alerts"
                  description="Show new bot signals on this dashboard."
                  enabled={settings.signal_alerts_enabled !== false} busy={settingsBusy === 'signal_alerts_enabled'}
                  onToggle={(v) => handleSettingToggle('signal_alerts_enabled', v)} />
                <ToggleRow testid="toggle-stocks" label="Stocks"
                  description="Allow stock signals to execute."
                  enabled={settings.stocks_enabled !== false} busy={settingsBusy === 'stocks_enabled'}
                  onToggle={(v) => handleSettingToggle('stocks_enabled', v)} />
                <ToggleRow testid="toggle-options" label="Options" description="Available after beta." enabled={false} locked />
              </div>
            </div>

            <div className="bg-[#101012] border border-[rgba(201,162,75,0.16)] rounded-md p-5 flex flex-col gap-3" data-testid="alpaca-connection-panel">
              <span className="text-[10px] tracking-[0.3em] font-mono text-[#A99563] uppercase">Connection</span>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${connected ? 'bg-[#7FC58C] dot-pulse' : 'bg-[#A99563]'}`} />
                  <span className={`text-sm font-mono ${connected ? 'text-[#F1ECDF]' : 'text-[#A99563]'}`} data-testid="alpaca-status-pill">
                    {connected ? 'Connected · Alpaca Paper' : 'Not connected'}
                  </span>
                </div>
                {connected ? (
                  <button onClick={handleDisconnect} disabled={actionBusy} data-testid="alpaca-disconnect-btn"
                    className="text-xs gold-text hover:text-[#E8C874] inline-flex items-center gap-1 disabled:opacity-60">
                    <Unplug size={12} /> Manage
                  </button>
                ) : (
                  <a href="/api/alpaca/oauth/start" data-testid="alpaca-connect-btn"
                    className="text-xs font-semibold px-3 py-1.5 bg-[#C9A24B] text-[#1a1407] hover:bg-[#E8C874] rounded-sm inline-flex items-center gap-1">
                    Connect <ArrowRight size={11} />
                  </a>
                )}
              </div>
              <a
                href="https://www.youtube.com/watch?v=3Z48kwbaKmo"
                target="_blank" rel="noopener noreferrer"
                data-testid="dashboard-setup-link"
                className="text-xs text-[#CDBE94] hover:gold-text inline-flex items-center gap-1.5"
              >
                <Activity size={12} className="gold-text" /> 2-min setup guide
              </a>
              <button onClick={handleTestSignal} disabled={actionBusy} data-testid="generate-test-signal-btn"
                className="text-xs px-3 py-2 border border-[rgba(201,162,75,0.16)] gold-text hover:bg-[#C9A24B]/10 rounded-sm inline-flex items-center justify-center gap-1.5 mt-1 disabled:opacity-60">
                <Sparkles size={12} /> Generate Test Signal
              </button>
            </div>
          </div>

          {/* ----- Merged Signals section ----- */}
          <div className="mt-5 bg-[#101012] border border-[rgba(201,162,75,0.16)] rounded-md p-5" data-testid="signals-section">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.3em] font-mono text-[#A99563] uppercase">Signals</span>
              <span className="text-[10px] font-mono text-[#A99563]">{signalHistory.length} recent</span>
            </div>

            {/* Latest highlighted */}
            <div className="mt-3 pb-4 border-b border-[rgba(201,162,75,0.16)]" data-testid="latest-signal-panel">
              {latestSignal ? (
                <div className="flex items-baseline flex-wrap gap-x-4 gap-y-1 font-mono">
                  <span className="text-lg text-[#F1ECDF]" data-testid="latest-signal-symbol">{latestSignal.symbol}</span>
                  <span className={`text-sm ${(latestSignal.side === 'buy') ? 'pnl-positive' : 'pnl-negative'}`} data-testid="latest-signal-side">
                    {latestSignal.side?.toUpperCase()}
                  </span>
                  {(latestSignal.reference_price || latestSignal.entry_price) && (
                    <span className="text-sm tabular-nums text-[#CDBE94]">${(latestSignal.reference_price || latestSignal.entry_price).toFixed(2)}</span>
                  )}
                  <span className="text-xs text-[#A99563]">{fmtTime(latestSignal.created_at)}</span>
                  {latestSignal.message && (
                    <span className="block w-full text-xs text-[#CDBE94] mt-1.5" data-testid="latest-signal-message">{latestSignal.message}</span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-[#A99563]">No signals yet</p>
              )}
            </div>

            {/* Recent list */}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.25em] font-mono text-[#A99563] border-b border-[rgba(201,162,75,0.08)]">
                    <th className="py-2 pr-3">SYMBOL</th>
                    <th className="py-2 pr-3">EVENT</th>
                    <th className="py-2 pr-3">SIDE</th>
                    <th className="py-2 pr-3">YOUR STATUS</th>
                    <th className="py-2 pr-3">DETAIL</th>
                    <th className="py-2 pr-3">QTY · LIMIT</th>
                    <th className="py-2">WHEN</th>
                  </tr>
                </thead>
                <tbody data-testid="signal-history-tbody">
                  {signalHistory.map((s) => {
                    const myExec = execBySignal[s.signal_id];
                    const status = myExec?.status;
                    const meta = status && EXEC_STATUS[status];
                    const reason = myExec?.skip_reason ? SKIP_LABEL[myExec.skip_reason] || myExec.skip_reason : myExec?.error_message;
                    const isExit = s.event_type === 'exit';
                    const sideCls = s.side === 'buy' ? 'pnl-positive' : 'pnl-negative';
                    return (
                      <tr key={s.signal_id} className="border-b border-[rgba(201,162,75,0.06)] last:border-0" data-testid={`history-row-${s.signal_id}`}>
                        <td className="py-2 pr-3 font-mono text-[#F1ECDF]">{s.symbol}</td>
                        <td className="py-2 pr-3 font-mono text-[#CDBE94]">
                          {isExit ? `EXIT${s.close_pct ? ` ${Math.round(s.close_pct * 100)}%` : ''}` : 'ENTRY'}
                        </td>
                        <td className={`py-2 pr-3 font-mono ${sideCls}`}>{(s.side || '').toUpperCase()}</td>
                        <td className="py-2 pr-3">
                          {meta ? (
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm ${
                              meta.tone === 'ok' ? 'border-[#7FC58C]/30 text-[#7FC58C]'
                              : meta.tone === 'warn' ? 'border-[#C9A24B]/40 gold-text'
                              : meta.tone === 'bad' ? 'border-[#E2706F]/40 pnl-negative'
                              : 'border-[rgba(201,162,75,0.16)] text-[#A99563]'
                            }`}>{isExit && meta.label === 'Filled' ? 'Closed' : (isExit && meta.label === 'Partially filled' ? 'Partially closed' : meta.label)}</span>
                          ) : (
                            <span className="text-[10px] font-mono text-[#A99563]">{isExit ? 'Exit signal received' : 'Signal received'}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-[11px] text-[#CDBE94] max-w-[200px] truncate" title={reason || ''}>{reason || (isExit && s.exit_reason ? s.exit_reason : '—')}</td>
                        <td className="py-2 pr-3 text-[11px] font-mono text-[#CDBE94]">
                          {myExec?.qty ? `${myExec.qty}${myExec.limit_price ? ` @ $${myExec.limit_price}` : ''}` : '—'}
                        </td>
                        <td className="py-2 text-[11px] font-mono text-[#A99563] whitespace-nowrap">{fmtDateShort(s.created_at)}</td>
                      </tr>
                    );
                  })}
                  {signalHistory.length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-[#A99563] text-xs">No signals yet. They&apos;ll appear here when the Talas bot sends them.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-10 text-center text-[11px] text-[#A99563] max-w-2xl mx-auto leading-relaxed">
            Talas beta is paper trading only. Talas Trading Co. is a software provider and does not provide personalized investment advice. Trading involves substantial risk.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ---------------- Reusable pieces ----------------
function Metric({ label, value, colorize, testid }) {
  const cls = colorize == null
    ? 'text-[#F1ECDF]'
    : colorize >= 0 ? 'pnl-positive' : 'pnl-negative';
  return (
    <div className="bg-[#101012] border border-[rgba(201,162,75,0.16)] rounded-md p-4 lift-on-hover" data-testid={testid}>
      <p className="text-[9px] tracking-[0.3em] font-mono text-[#A99563] uppercase">{label}</p>
      <p className={`mt-1.5 text-2xl font-mono font-semibold tabular-nums ${cls}`}>{value ?? '—'}</p>
    </div>
  );
}

function ToggleRow({ label, description, enabled, busy, onToggle, locked, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[#F1ECDF] font-medium">{label}</p>
        <p className="text-[11px] text-[#A99563] mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => !locked && !busy && onToggle(!enabled)}
        disabled={locked || busy}
        data-testid={testid}
        aria-pressed={enabled}
        className={`relative shrink-0 h-6 w-11 rounded-full border transition-all duration-200 ${
          enabled
            ? 'bg-[#C9A24B]/20 border-[#C9A24B]/60'
            : 'bg-[#0A0A0B] border-[rgba(201,162,75,0.25)]'
        } ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-[#C9A24B]/80'}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full transition-all duration-200 ${
            enabled ? 'left-[1.4rem] bg-[#C9A24B]' : 'left-0.5 bg-[#A99563]'
          }`}
        />
      </button>
    </div>
  );
}
